// One consignment in full (bottom sheet on phones, right panel on wider screens): challan with
// photos, the paper challan's hand-over, receiving status with Remind, and a link to the order.

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, CheckCircle2, FileText, Handshake, Loader2, Package } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { gateApi, type Consignment } from "@/lib/gate";
import { useGate } from "@/lib/session-scope";
import { useIsDesktop } from "@/lib/use-media";
import { cn } from "@/lib/utils";
import { AuthImage } from "@/components/auth-image";
import { StatusChip, waited } from "@/components/consignment-card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[34%_1fr] border-b border-border text-sm last:border-b-0">
      <div className="bg-amber-50/70 px-3 py-2 text-xs font-medium text-muted-foreground dark:bg-amber-950/30">
        {label}
      </div>
      <div className="min-w-0 px-3 py-2 break-words">{children}</div>
    </div>
  );
}

export function ConsignmentSheet({
  id,
  onClose,
  onChanged,
}: {
  id: number | null;
  onClose: () => void;
  /** After a reminder / hand-over, so the list behind refreshes. */
  onChanged?: () => void;
}) {
  const { token } = useAuth();
  const { gate } = useGate();
  const desktop = useIsDesktop();
  const [c, setC] = useState<Consignment | null>(null);
  const [handTo, setHandTo] = useState("");
  const [busy, setBusy] = useState<"remind" | "hand" | null>(null);
  const [zoom, setZoom] = useState<number | null>(null);
  // Callers pass inline callbacks; keep the latest in a ref so they never re-trigger the load.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const load = useCallback(async () => {
    if (id == null) return;
    try {
      const d = await gateApi.consignment(token, gate.id, id);
      if (d?.errorMessage) {
        toast.error(d.errorMessage);
        onCloseRef.current();
        return;
      }
      setC(d);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not open the consignment.");
      onCloseRef.current();
    }
  }, [id, token, gate.id]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset for the newly opened consignment
    setC(null);
    setHandTo("");
    void load();
  }, [load]);

  async function remind() {
    if (!c) return;
    setBusy("remind");
    try {
      const r = await gateApi.remind(token, gate.id, c.id);
      if (r?.errorMessage) toast.error(r.errorMessage);
      else {
        toast.success(r?.myMessage ?? "Reminder sent.");
        await load();
        onChanged?.();
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not send the reminder.");
    } finally {
      setBusy(null);
    }
  }

  async function handOver() {
    if (!c || !handTo.trim()) return;
    setBusy("hand");
    try {
      const r = await gateApi.handOverChallan(token, gate.id, c.id, handTo.trim());
      if (r?.errorMessage) toast.error(r.errorMessage);
      else {
        toast.success(r?.myMessage ?? "Recorded.");
        await load();
        onChanged?.();
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not record the hand-over.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <Sheet open={id != null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side={desktop ? "right" : "bottom"}
        className={cn("flex flex-col gap-0 p-0", desktop ? "w-full sm:max-w-xl" : "max-h-[92svh] rounded-t-2xl")}
      >
        {!desktop && (
          <div className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-muted-foreground/30" aria-hidden />
        )}
        <SheetHeader className="shrink-0 pr-12 pb-2">
          <SheetTitle className="text-lg">{c?.vendor ?? "Consignment"}</SheetTitle>
          <SheetDescription>
            {c ? (
              <>
                <span className="font-mono">{c.poNumber}</span> · consignment {c.consignmentNumber ?? "?"}
              </>
            ) : (
              ""
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-6">
          {!c ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-16 w-full rounded-2xl" />
              <Skeleton className="h-40 w-full rounded-2xl" />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {/* Receiving status first: is anyone coming for this truck? */}
              {c.status === "WAITING" ? (
                <div className="flex flex-col gap-2 rounded-2xl bg-amber-100 p-3 text-amber-950 dark:bg-amber-950 dark:text-amber-100">
                  <div className="font-semibold">Waiting to be received — {waited(c.waitingMinutes)}</div>
                  <div className="text-xs opacity-80">
                    {c.remindCount
                      ? `${c.remindCount} reminder(s) sent, last at ${c.lastRemindedAt}.`
                      : "The receivers were emailed when it arrived."}
                  </div>
                  <Button className="h-11 rounded-xl" disabled={busy != null} onClick={() => void remind()}>
                    {busy === "remind" ? <Loader2 className="animate-spin" /> : <BellRing />} Remind receiver
                  </Button>
                </div>
              ) : c.status === "RECEIVED" ? (
                <div className="flex items-start gap-2.5 rounded-2xl bg-emerald-100 p-3 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
                  <CheckCircle2 className="mt-0.5 size-5 shrink-0" />
                  <div className="text-sm">
                    <div className="font-semibold">Received{c.receivedGrn ? ` — ${c.receivedGrn}` : ""}</div>
                    <div className="text-xs opacity-80">{[c.receivedBy, c.receivedAt].filter(Boolean).join(" · ")}</div>
                  </div>
                </div>
              ) : (
                <StatusChip c={c} />
              )}

              {/* Challan */}
              <div className="flex flex-col gap-2 rounded-2xl border border-border bg-card p-3">
                <div className="flex items-center gap-1.5 text-sm font-semibold">
                  <FileText className="size-4 text-primary" /> Challan
                </div>
                {c.challanNumber ? (
                  <div className="text-base">
                    <span className="font-mono font-semibold">{c.challanNumber}</span>
                    {c.challanDate && <span className="text-sm text-muted-foreground"> · {c.challanDate}</span>}
                  </div>
                ) : c.noChallanReason ? (
                  <div className="text-sm text-destructive">No challan — {c.noChallanReason}</div>
                ) : !c.photoIds.length ? (
                  <div className="text-sm text-muted-foreground">Not recorded.</div>
                ) : null}
                {c.photoIds.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {c.photoIds.map((pid, i) => (
                      <Button
                        key={pid}
                        variant="ghost"
                        className="aspect-square h-auto overflow-hidden rounded-xl border border-border p-0"
                        aria-label={`Open challan photo ${i + 1}`}
                        onClick={() => setZoom(pid)}
                      >
                        <AuthImage
                          path={gateApi.photoPath(gate.id, pid)}
                          alt={`Challan photo ${i + 1}`}
                          className="size-full object-cover"
                        />
                      </Button>
                    ))}
                  </div>
                )}
              </div>

              {/* Paper challan: collected at the gate → handed over. */}
              {c.challanCollected && (
                <div className="flex flex-col gap-2 rounded-2xl border border-border bg-card p-3">
                  <div className="flex items-center gap-1.5 text-sm font-semibold">
                    <Handshake className="size-4 text-primary" /> Original challan
                  </div>
                  {c.challanHandedOverTo ? (
                    <div className="text-sm">
                      Handed over to <span className="font-semibold">{c.challanHandedOverTo}</span>
                      <span className="text-muted-foreground"> · {c.challanHandedOverAt}</span>
                    </div>
                  ) : (
                    <>
                      <div className="text-sm text-amber-800 dark:text-amber-300">
                        With the gate — hand it to the receiver.
                      </div>
                      <form
                        className="flex gap-2"
                        onSubmit={(e) => {
                          e.preventDefault();
                          void handOver();
                        }}
                      >
                        <Input
                          value={handTo}
                          onChange={(e) => setHandTo(e.target.value)}
                          placeholder="Handed over to (name)"
                          autoCapitalize="words"
                          className="h-11 rounded-xl text-base md:text-base"
                        />
                        <Button
                          type="submit"
                          className="h-11 shrink-0 rounded-xl"
                          disabled={busy != null || !handTo.trim()}
                        >
                          {busy === "hand" && <Loader2 className="animate-spin" />} Save
                        </Button>
                      </form>
                    </>
                  )}
                </div>
              )}

              <div className="overflow-hidden rounded-2xl border border-border bg-card">
                <Fact label="Arrived">{c.arrivedAt}</Fact>
                <Fact label="Store">{c.store ?? "—"}</Fact>
                <Fact label="Vehicle">
                  {c.vehicleNumber ? <span className="font-mono">{c.vehicleNumber}</span> : "—"}
                </Fact>
                <Fact label="Driver">{c.driverName ?? "—"}</Fact>
                {c.remarks && <Fact label="Remarks">{c.remarks}</Fact>}
                <Fact label="Recorded by">{[c.enteredBy, c.gate].filter(Boolean).join(" · ") || "—"}</Fact>
              </div>

              <Button
                variant="outline"
                className="h-11 rounded-xl"
                nativeButton={false}
                render={<Link to={`/order/${c.purchaseOrderId}`} onClick={onClose} />}
              >
                <Package /> Open the order · all consignments
              </Button>
            </div>
          )}
        </div>

        <Dialog open={zoom != null} onOpenChange={(o) => !o && setZoom(null)}>
          <DialogContent className="max-w-[min(96vw,56rem)] p-2">
            <DialogTitle className="sr-only">Challan photo</DialogTitle>
            {zoom != null && (
              <AuthImage
                path={gateApi.photoPath(gate.id, zoom)}
                alt="Challan photo"
                className="max-h-[85svh] w-full rounded-lg object-contain"
              />
            )}
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}
